import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Bitcoin,
  CreditCard,
  Copy,
  Check,
  Loader2,
  ExternalLink,
  Clock3,
  ShieldCheck,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface WalletOption {
  network: string;
  label: string;
  address: string;
}

interface ManualCryptoPayment {
  id: string;
  order_id: string;
  amount_usd: number;
  network: string;
  wallet_address: string;
  deadline: string;
  tx_hash: string | null;
  status: string;
  admin_notes: string | null;
}

interface Props {
  briefId: string;
  amount: number;
  onGenerateBinance: () => void;
  generatingBinance: boolean;
}

type Method = 'stripe' | 'binance' | 'manual';

const LaunchKitPaymentMethods = ({ briefId, amount, onGenerateBinance, generatingBinance }: Props) => {
  const [method, setMethod] = useState<Method>('stripe');
  const [stripeLoading, setStripeLoading] = useState(false);

  const handleStripe = async () => {
    setStripeLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-launch-kit-brief', {
        body: { brief_id: briefId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (!data?.url) throw new Error('No checkout URL returned');
      window.location.href = data.url;
    } catch (e: any) {
      toast.error(e?.message || 'Could not start Stripe checkout');
      setStripeLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <MethodTab active={method === 'stripe'} onClick={() => setMethod('stripe')} icon={<CreditCard className="w-4 h-4" />} label="Card (Stripe)" />
        <MethodTab active={method === 'binance'} onClick={() => setMethod('binance')} icon={<Bitcoin className="w-4 h-4" />} label="Binance Pay" />
        <MethodTab active={method === 'manual'} onClick={() => setMethod('manual')} icon={<Bitcoin className="w-4 h-4" />} label="Manual Crypto" />
      </div>

      {method === 'stripe' && (
        <div className="space-y-3">
          <Button
            onClick={handleStripe}
            disabled={stripeLoading}
            size="lg"
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white h-14 text-base font-semibold"
          >
            {stripeLoading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CreditCard className="w-5 h-5 mr-2" />}
            Pay ${amount.toLocaleString()} with Card
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-xs text-center text-slate-500">
            Secure Stripe Checkout. Visa, Mastercard, Amex, Apple Pay, Google Pay.
          </p>
        </div>
      )}

      {method === 'binance' && (
        <div className="space-y-3">
          <Button
            onClick={onGenerateBinance}
            disabled={generatingBinance}
            size="lg"
            className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white h-14 text-base font-semibold"
          >
            {generatingBinance ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Bitcoin className="w-5 h-5 mr-2" />}
            Pay ${amount.toLocaleString()} with Binance Pay
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-xs text-center text-slate-500">
            Generates a secure checkout link to our Binance Pay merchant. Requires a Binance account.
          </p>
        </div>
      )}

      {method === 'manual' && (
        <ManualCryptoFlow briefId={briefId} amount={amount} />
      )}
    </div>
  );
};

const MethodTab = ({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium border transition-all ${
      active
        ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
        : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:bg-blue-50/50'
    }`}
  >
    {icon}
    {label}
  </button>
);

const ManualCryptoFlow = ({ briefId, amount }: { briefId: string; amount: number }) => {
  const [wallets, setWallets] = useState<WalletOption[]>([]);
  const [existing, setExisting] = useState<ManualCryptoPayment | null>(null);
  const [loading, setLoading] = useState(true);
  const [network, setNetwork] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      const { data: setting } = await supabase
        .from('admin_settings')
        .select('value')
        .eq('key', 'crypto_wallets')
        .maybeSingle();
      if (setting?.value) {
        try {
          const arr = JSON.parse(setting.value) as WalletOption[];
          setWallets(arr.filter(w => w.address && w.address.trim().length > 0));
        } catch { /* ignore */ }
      }
      const { data: pay } = await supabase
        .from('manual_crypto_payments')
        .select('*')
        .eq('brief_id', briefId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (pay) setExisting(pay as ManualCryptoPayment);
      setLoading(false);
    })();
  }, [briefId]);

  const handleCreate = async () => {
    if (!network) { toast.error('Choose a network'); return; }
    const wallet = wallets.find(w => w.network === network);
    if (!wallet) { toast.error('Wallet not configured'); return; }
    setCreating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const orderId = `LK-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
      const deadline = new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('manual_crypto_payments')
        .insert({
          brief_id: briefId,
          user_id: user.id,
          order_id: orderId,
          amount_usd: amount,
          network: wallet.network,
          wallet_address: wallet.address,
          deadline,
          status: 'awaiting_payment',
        })
        .select()
        .single();
      if (error) throw error;
      setExisting(data as ManualCryptoPayment);
      toast.success('Payment order created');
    } catch (e: any) {
      toast.error(e?.message || 'Could not create order');
    } finally {
      setCreating(false);
    }
  };

  const handleSubmitHash = async () => {
    if (!existing) return;
    const trimmed = txHash.trim();
    if (trimmed.length < 10) { toast.error('Paste a valid transaction hash'); return; }
    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('manual_crypto_payments')
        .update({ tx_hash: trimmed, status: 'submitted', submitted_at: new Date().toISOString() })
        .eq('id', existing.id);
      if (error) throw error;
      setExisting({ ...existing, tx_hash: trimmed, status: 'submitted' });
      toast.success('Transaction submitted — our team will verify within a few hours');
    } catch (e: any) {
      toast.error(e?.message || 'Could not submit');
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>;
  }

  if (wallets.length === 0 && !existing) {
    return (
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-slate-700">
          <p className="font-semibold text-slate-900">Manual crypto temporarily unavailable</p>
          <p>Our wallets are being rotated. Please use Card or Binance Pay, or contact us to arrange a wire.</p>
        </div>
      </div>
    );
  }

  if (existing) {
    const expired = new Date(existing.deadline).getTime() < Date.now();
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
        {existing.status === 'verified' && (
          <div className="rounded-xl bg-green-50 border border-green-200 p-4 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-700">
              <p className="font-semibold text-slate-900">Payment verified</p>
              <p>Your crypto payment has been confirmed. Production starts now.</p>
            </div>
          </div>
        )}

        {existing.status === 'rejected' && (
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 space-y-2">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-700">
                <p className="font-semibold text-slate-900">Payment could not be verified</p>
                {existing.admin_notes && <p className="mt-1">{existing.admin_notes}</p>}
                <p className="mt-1">Contact us at <a className="text-blue-600 underline" href="mailto:support@facelessaura.club">support@facelessaura.club</a> with proof.</p>
              </div>
            </div>
          </div>
        )}

        {existing.status === 'submitted' && (
          <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 flex items-start gap-3">
            <Clock3 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5 animate-pulse" />
            <div className="text-sm text-slate-700">
              <p className="font-semibold text-slate-900">Verifying on-chain — usually within a few hours</p>
              <p className="break-all mt-1 text-xs">TX: {existing.tx_hash}</p>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Order ID</p>
              <p className="font-mono text-sm text-slate-900">{existing.order_id}</p>
            </div>
            <div className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
              expired && existing.status === 'awaiting_payment' ? 'bg-rose-100 text-rose-700' :
              existing.status === 'awaiting_payment' ? 'bg-amber-100 text-amber-700' :
              existing.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
              existing.status === 'verified' ? 'bg-green-100 text-green-700' :
              'bg-rose-100 text-rose-700'
            }`}>
              {expired && existing.status === 'awaiting_payment' ? 'Expired' : existing.status.replace('_', ' ')}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount (USD)" value={`$${Number(existing.amount_usd).toLocaleString()}`} />
            <Field label="Network" value={existing.network} />
          </div>

          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Send exactly to</p>
            <div className="flex items-stretch gap-2">
              <div className="flex-1 font-mono text-xs sm:text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 break-all">
                {existing.wallet_address}
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => copy(existing.wallet_address)} className="self-stretch px-3">
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div className="text-xs text-slate-500 flex items-center gap-1.5">
            <Clock3 className="w-3.5 h-3.5" />
            Valid until {new Date(existing.deadline).toLocaleString()}
          </div>

          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
            ⚠ Send the <strong>exact USD value in the matching token on the chosen network</strong>. Wrong network = lost funds. Include nothing in the memo field.
          </div>
        </div>

        {(existing.status === 'awaiting_payment' || existing.status === 'rejected') && !expired && (
          <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
            <Label>Paste your transaction hash</Label>
            <Input
              placeholder="0x… or T…"
              value={txHash}
              onChange={(e) => setTxHash(e.target.value)}
              className="font-mono text-xs"
            />
            <Button
              onClick={handleSubmitHash}
              disabled={submitting || txHash.trim().length < 10}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Submit transaction hash
            </Button>
            <p className="text-xs text-slate-500">
              Our team verifies on-chain manually. You'll be notified by email once confirmed.
            </p>
          </div>
        )}
      </motion.div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
      <div>
        <p className="text-sm font-semibold text-slate-900 mb-1">Choose a network</p>
        <p className="text-xs text-slate-500">Pay with any wallet that supports the selected network.</p>
      </div>
      <div className="space-y-2">
        <Label>Network</Label>
        <Select value={network} onValueChange={setNetwork}>
          <SelectTrigger><SelectValue placeholder="Select a network" /></SelectTrigger>
          <SelectContent>
            {wallets.map(w => (
              <SelectItem key={w.network} value={w.network}>{w.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button onClick={handleCreate} disabled={!network || creating} className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12">
        {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bitcoin className="w-4 h-4 mr-2" />}
        Generate payment instructions
      </Button>
      <p className="text-xs text-slate-500 text-center">You'll get a wallet address valid for 2 hours.</p>
    </div>
  );
};

const Field = ({ label, value }: { label: string; value: string }) => (
  <div>
    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p>
    <p className="text-sm font-semibold text-slate-900 mt-0.5">{value}</p>
  </div>
);

export default LaunchKitPaymentMethods;
